import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Mail, MapPin, Phone } from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { cities, joinList } from '@/constants/cities'
import { services } from '@/constants/services'
import { siteConfig } from '@/constants/site'

const linkClass = 'inline-block rounded-sm py-1 text-ink-foreground/80 transition-colors hover:text-ink-foreground hover:underline underline-offset-4'

const headingClass = 'font-mono text-xs tracking-[0.16em] text-ink-muted uppercase'

export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='surface-ink'>
            <div className='shell grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-12 lg:gap-x-8 lg:py-20'>
                <div className='md:col-span-2 lg:col-span-4'>
                    <Logo inverse />

                    <p className='mt-5 max-w-sm leading-relaxed text-ink-muted'>
                        Rent-O-Tech rents laptops and desktops, maintains IT hardware under AMC and sells refurbished business laptops to companies across Delhi NCR.
                    </p>

                    <ul className='mt-8 space-y-3'>
                        <li>
                            <a href={siteConfig.phone.href} className='inline-flex items-center gap-3 rounded-sm py-1 font-medium hover:underline underline-offset-4'>
                                <Phone aria-hidden='true' className='size-4 text-primary' />
                                {siteConfig.phone.display}
                            </a>
                        </li>

                        <li>
                            <a href={`mailto:${siteConfig.email}`} className='inline-flex items-center gap-3 rounded-sm py-1 font-medium hover:underline underline-offset-4'>
                                <Mail aria-hidden='true' className='size-4 text-primary' />
                                {siteConfig.email}
                            </a>
                        </li>

                        <li className='flex items-start gap-3 py-1 text-ink-muted'>
                            <MapPin aria-hidden='true' className='mt-1 size-4 shrink-0 text-primary' />
                            Serving {joinList(cities.map((city) => city.name))}
                        </li>
                    </ul>
                </div>

                <nav aria-labelledby='footer-services' className='lg:col-span-3'>
                    <h2 id='footer-services' className={headingClass}>
                        Services
                    </h2>

                    <ul className='mt-5 space-y-1.5'>
                        {services.map((service) => (
                            <li key={service.slug}>
                                <Link href={`/${service.slug}`} className={linkClass}>
                                    {service.name}
                                </Link>
                            </li>
                        ))}

                        <li>
                            <Link href='/contact' className={linkClass}>
                                Get a free quote
                            </Link>
                        </li>
                    </ul>
                </nav>

                <nav aria-labelledby='footer-cities' className='lg:col-span-3'>
                    <h2 id='footer-cities' className={headingClass}>
                        Laptop on rent near you
                    </h2>

                    <ul className='mt-5 space-y-1.5'>
                        {cities.map((city) => (
                            <li key={city.slug}>
                                <Link href={`/laptop-rental/${city.slug}`} className={linkClass}>
                                    Laptop on rent in {city.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <nav aria-labelledby='footer-company' className='lg:col-span-2'>
                    <h2 id='footer-company' className={headingClass}>
                        Company
                    </h2>

                    <ul className='mt-5 space-y-1.5'>
                        <li>
                            <Link href='/contact' className={linkClass}>
                                Contact
                            </Link>
                        </li>

                        <li>
                            <Link href='/privacy-policy' className={linkClass}>
                                Privacy policy
                            </Link>
                        </li>
                    </ul>
                </nav>
            </div>

            <div className='border-t border-ink-border'>
                <div className='shell flex flex-col gap-2 py-6 text-sm text-ink-muted sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © <span suppressHydrationWarning>{new Date().getFullYear()}</span> {siteConfig.name}. All rights reserved.
                    </p>

                    <p>
                        Laptop rental, desktop rental, IT hardware AMC and refurbished laptops in {siteConfig.region}.
                    </p>
                </div>
            </div>
        </footer>
    )
}
